import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowClockwise, Checks, CircleNotch, ClipboardText, Pause, WarningCircle, X } from '@phosphor-icons/react';
import {
  BestaetigenDialog, ImmoButton, LeerZustand, ObjektPicker, SchwebenderToast, SegmentedControl, useToastPill, type ObjektPickerOption,
} from '../../../../components/immobilien/ui';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { meldeDokumenteGeaendert } from '../../../../lib/dokumente/useDokumenteKopf';
import {
  ladeMassenKandidaten, ladeSeite, ladeStichprobe, legeAltarchivAb, PRUEF_SEITE, setzeStatus, stichprobeBestanden, STICHPROBE_MINDEST,
  STICHPROBE_MITTEL_MINDEST, zaehleSegmente, zurueckStatus, type MassenStufe, type PruefSegment, type PruefZeile, type SicherheitsFilter, type Stichprobe,
} from '../../../../lib/dokumente/altarchivPruefung';
import { useOrdnerQuelle } from '../ablage/ordnerQuelle';
import { PruefKarte, type PruefAktionen } from './PruefKarte';

const fehlerText = (e: unknown, sonst: string) => (e instanceof Error && e.message ? e.message : sonst);
const zahl = (n: number) => n.toLocaleString('de-DE');

interface MassenStand { aktuell: number; gesamt: number; ok: number; fehler: number; ersterFehler: string | null; stopp: boolean }

/**
 * Prüfen der Altarchiv-Vorschläge (#517, Etappe C/D): Karten je PDF, Übernehmen ins Google Drive
 * (Objekt/Altarchiv/…), Ablehnen mit Rückgängig, Massenablage je Objekt erst nach bestandener Stichprobe.
 */
export function PruefBereich({ userId, onOeffneInAkten }: { userId: string | null; onOeffneInAkten: (objektId: string | null, dateiId: string) => void }) {
  const { mobil } = useMobilModus();
  const ordner = useOrdnerQuelle();
  const [toast, zeigeToast] = useToastPill(3200);

  const [segment, setSegment] = useState<PruefSegment>('vorschlag');
  const [objektId, setObjektId] = useState<string | null>(null);
  const [sicherheit, setSicherheit] = useState<SicherheitsFilter>('alle');
  const [zeilen, setZeilen] = useState<PruefZeile[] | null>(null);
  const [anzahl, setAnzahl] = useState<number | null>(null);
  const [zaehler, setZaehler] = useState<Record<PruefSegment, number> | null>(null);
  const [stichprobe, setStichprobe] = useState<Stichprobe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedtMehr, setLaedtMehr] = useState(false);
  const [runde, setRunde] = useState(0);
  const token = useRef(0);

  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  useEffect(() => { ladeObjekte().then(setObjekte).catch(() => setObjekte([])); }, []);
  const pickerOptionen: ObjektPickerOption[] = useMemo(() => objekte.map(o => ({
    id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '),
    suchtext: `${o.nummer} ${o.ort}`, hinweis: o.driveId ? undefined : 'kein Drive-Ordner',
  })), [objekte]);

  const filter = useMemo(() => ({ segment, objektId, sicherheit }), [segment, objektId, sicherheit]);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);

  useEffect(() => {
    const t = ++token.current;
    setZeilen(null); setFehler(null);
    ladeSeite(filter, 0)
      .then(r => { if (t === token.current) { setZeilen(r.zeilen); setAnzahl(r.anzahl); } })
      .catch((e: unknown) => { if (t === token.current) setFehler(fehlerText(e, 'Vorschläge nicht geladen')); });
    zaehleSegmente(objektId).then(z => { if (t === token.current) setZaehler(z); }).catch(() => undefined);
    ladeStichprobe().then(setStichprobe).catch(() => undefined);
  }, [filter, objektId, runde]);

  const mehr = async () => {
    if (!zeilen) return;
    const t = token.current;
    setLaedtMehr(true);
    try {
      const r = await ladeSeite(filter, zeilen.length);
      if (t === token.current) setZeilen(alt => [...(alt ?? []), ...r.zeilen]);
    } catch (e) { zeigeToast(fehlerText(e, 'Weitere nicht geladen'), true); } finally { setLaedtMehr(false); }
  };

  const entferne = (id: string) => {
    setZeilen(alt => (alt ?? []).filter(z => z.dokument_id !== id));
    setAnzahl(a => (a !== null ? Math.max(0, a - 1) : a));
  };
  const nachAktion = () => {
    meldeDokumenteGeaendert();
    zaehleSegmente(objektId).then(setZaehler).catch(() => undefined);
    ladeStichprobe().then(setStichprobe).catch(() => undefined);
  };

  const aktionen: PruefAktionen = {
    onUebernehmen: async (z, w) => {
      const r = await legeAltarchivAb(z.dokument_id, w);
      entferne(z.dokument_id);
      nachAktion();
      if (r.schonAbgelegt) zeigeToast('Diese Datei war bereits übernommen.');
      else if (r.statusFehler) zeigeToast('Übernommen, aber der Status konnte nicht gespeichert werden.', true);
      else zeigeToast(`Übernommen: ${w.dateiname.trim()}`, false, { label: 'Öffnen', onClick: () => onOeffneInAkten(w.objektId, r.gdriveFileId) });
    },
    onAblehnen: async z => {
      await setzeStatus(z.dokument_id, 'abgelehnt', userId);
      entferne(z.dokument_id);
      nachAktion();
      zeigeToast('Vorschlag abgelehnt', false, {
        label: 'Rückgängig',
        onClick: () => { setzeStatus(z.dokument_id, zurueckStatus(z), userId).then(() => { neuLaden(); nachAktion(); }).catch((e: unknown) => zeigeToast(fehlerText(e, 'Rückgängig fehlgeschlagen'), true)); },
      });
    },
    onZurueck: async z => {
      await setzeStatus(z.dokument_id, zurueckStatus(z), userId);
      entferne(z.dokument_id);
      nachAktion();
      zeigeToast('Zurück in der Prüfung');
    },
    onOeffnen: z => z.gdrive_file_id && onOeffneInAkten(z.objekt_id, z.gdrive_file_id),
  };

  // ── Massenablage (je Objekt, erst nach der Stichprobe) ──────────────────
  // Stufe der Massenablage folgt dem Sicherheitsfilter: „Eher sicher“ hat eine eigene, zweite Stichprobe.
  const stufe: MassenStufe = sicherheit === 'mittel' ? 'mittel' : 'hoch';
  const sp = stichprobe ? stichprobe[stufe] : null;
  const bestanden = !!sp && stichprobeBestanden(sp, stufe);
  const mindest = stufe === 'hoch' ? STICHPROBE_MINDEST : STICHPROBE_MITTEL_MINDEST;
  const stufenText = stufe === 'hoch' ? 'sehr sicheren' : 'eher sicheren';
  const [frage, setFrage] = useState<number | null>(null);
  const [massen, setMassen] = useState<MassenStand | null>(null);
  const stoppRef = useRef(false);
  const objektName = objekte.find(o => o.id === objektId)?.name ?? '';

  const frageMassen = async () => {
    if (!objektId) return;
    try { setFrage((await ladeMassenKandidaten(objektId, stufe)).length); } catch (e) { zeigeToast(fehlerText(e, 'Vorschläge nicht geladen'), true); }
  };
  const starteMassen = async () => {
    if (!objektId) return;
    setFrage(null);
    stoppRef.current = false;
    let kandidaten: PruefZeile[];
    try { kandidaten = await ladeMassenKandidaten(objektId, stufe); } catch (e) { zeigeToast(fehlerText(e, 'Vorschläge nicht geladen'), true); return; }
    let ok = 0; let fehl = 0; let erster: string | null = null;
    for (let i = 0; i < kandidaten.length; i++) {
      if (stoppRef.current) break;
      const z = kandidaten[i];
      setMassen({ aktuell: i + 1, gesamt: kandidaten.length, ok, fehler: fehl, ersterFehler: erster, stopp: false });
      try {
        await legeAltarchivAb(z.dokument_id, { objektId, zielPfad: z.ziel_pfad ?? '', dateiname: z.neuer_name ?? '' }, true);
        ok++;
      } catch (e) { fehl++; erster ??= `${z.neuer_name}: ${fehlerText(e, 'fehlgeschlagen')}`; }
    }
    setMassen(null);
    neuLaden();
    nachAktion();
    zeigeToast(fehl ? `${ok} übernommen, ${fehl} fehlgeschlagen – ${erster}` : `${ok} ${ok === 1 ? 'Datei' : 'Dateien'} übernommen${stoppRef.current ? ' (angehalten)' : ''}`, fehl > 0);
  };
  useEffect(() => {
    if (!massen) return;
    const warnen = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [massen]);

  const z = zaehler;
  const hoehe = mobil ? 44 : 34;
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
        <SegmentedControl<PruefSegment> ariaLabel="Status der Vorschläge" art="akzent" wert={segment} onChange={setSegment} hoehe={hoehe}
          segmente={[
            { key: 'vorschlag', label: 'Zu prüfen', anzahl: z?.vorschlag },
            { key: 'ohne_objekt', label: 'Ohne Objekt', anzahl: z?.ohne_objekt },
            { key: 'abgelegt', label: 'Übernommen', anzahl: z?.abgelegt },
            { key: 'abgelehnt', label: 'Abgelehnt', anzahl: z?.abgelehnt },
          ]} />
        <div className="flex items-center" style={{ gap: 6 }}>
          <ObjektPicker optionen={pickerOptionen} wert={objektId} onWahl={setObjektId} leerLabel="Alle Objekte" />
          {objektId && (
            <button type="button" aria-label="Objekt-Filter entfernen" onClick={() => setObjektId(null)} className="flex items-center justify-center cursor-pointer"
              style={{ width: 44, height: 44, borderRadius: 14, border: 'none', background: 'var(--fill2)', color: 'var(--ink2)' }}><X weight="bold" size={13} /></button>
          )}
        </div>
        {segment === 'vorschlag' && (
          <SegmentedControl<SicherheitsFilter> ariaLabel="Sicherheit" wert={sicherheit} onChange={setSicherheit} hoehe={hoehe}
            segmente={[{ key: 'alle', label: 'Alle' }, { key: 'hoch', label: 'Sehr sicher' }, { key: 'mittel', label: 'Eher sicher' }, { key: 'niedrig', label: 'Unsicher' }]} />
        )}
      </div>

      {segment === 'vorschlag' && sp && sicherheit !== 'niedrig' && (
        <div className="immo-innen flex items-center flex-wrap" style={{ borderRadius: 18, padding: '10px 14px', gap: 10 }}>
          <ClipboardText weight="fill" size={18} color={bestanden ? 'var(--ok)' : 'var(--acc)'} />
          <span className="flex-1" style={{ fontSize: 13, color: 'var(--ink2)', minWidth: 200 }}>
            {bestanden
              ? `Stichprobe „${stufe === 'hoch' ? 'sehr sicher' : 'eher sicher'}“ bestanden (${zahl(sp.einzeln)} einzeln übernommen, ${zahl(sp.korrigiert)} korrigiert). Die Massenablage ist frei – Objekt wählen.`
              : `Stichprobe „${stufe === 'hoch' ? 'sehr sicher' : 'eher sicher'}“: ${zahl(sp.einzeln)} von ${mindest} ${stufenText} Vorschlägen einzeln übernommen, ${zahl(sp.korrigiert)} davon korrigiert. Die Massenablage wird frei, wenn höchstens 2 korrigiert werden mussten.`}
          </span>
          {bestanden && objektId && (
            <ImmoButton art="akzent" groesse={44} icon={Checks} disabled={!!massen} onClick={() => void frageMassen()}>{stufe === 'hoch' ? 'Sehr sichere übernehmen' : 'Eher sichere übernehmen'}</ImmoButton>
          )}
        </div>
      )}

      {massen && (
        <div role="status" aria-live="polite" className="immo-glas flex flex-col" style={{ borderRadius: 22, padding: 14, gap: 8, position: 'sticky', top: 12, zIndex: 10 }}>
          <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
            <CircleNotch size={16} className="animate-spin" color="var(--acc)" />
            <span className="flex-1 immo-zahl" style={{ fontSize: 13, fontWeight: 800, minWidth: 160 }}>
              Übernehme: {zahl(massen.aktuell)} von {zahl(massen.gesamt)}{massen.fehler > 0 && <span style={{ color: 'var(--bad)' }}> · {massen.fehler} Fehler</span>}
            </span>
            <ImmoButton groesse={44} icon={Pause} disabled={massen.stopp} onClick={() => { stoppRef.current = true; setMassen(m => (m ? { ...m, stopp: true } : m)); }}>
              {massen.stopp ? 'Hält nach dieser Datei an …' : 'Anhalten'}
            </ImmoButton>
          </div>
          <span style={{ height: 6, borderRadius: 3, background: 'var(--fill3)', overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${massen.gesamt ? Math.round((massen.aktuell / massen.gesamt) * 100) : 0}%`, background: 'var(--acc)', transition: 'width .3s' }} />
          </span>
          <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Bitte diese Seite geöffnet lassen, bis die Übernahme fertig ist.</span>
        </div>
      )}

      {fehler ? (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 22, padding: 16, gap: 12 }}>
          <WarningCircle weight="fill" size={22} color="var(--bad)" /><span className="flex-1" style={{ fontSize: 13 }}>{fehler}</span>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      ) : !zeilen ? (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))', gap: 14 }} aria-busy="true">
          {Array.from({ length: 4 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 300, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </div>
      ) : zeilen.length === 0 ? (
        <LeerZustand icon={ClipboardText}
          titel={segment === 'vorschlag' ? 'Nichts zu prüfen' : segment === 'ohne_objekt' ? 'Keine Dateien ohne Objekt' : segment === 'abgelegt' ? 'Noch nichts übernommen' : 'Nichts abgelehnt'}
          text={objektId || sicherheit !== 'alle' ? 'Für diesen Filter gibt es keine Einträge.' : undefined} />
      ) : (
        <>
          {anzahl !== null && <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{zahl(anzahl)} {anzahl === 1 ? 'Datei' : 'Dateien'}</span>}
          <div className="grid items-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 440px), 1fr))', gap: 14 }}>
            {zeilen.map(zeile => (
              <PruefKarte key={zeile.dokument_id} z={zeile} objekte={objekte} pickerOptionen={pickerOptionen} ordner={ordner} aktionen={aktionen} gesperrt={!!massen} mobil={mobil} />
            ))}
          </div>
          {anzahl !== null && zeilen.length < anzahl && zeilen.length >= PRUEF_SEITE && (
            <ImmoButton groesse={44} icon={ArrowClockwise} disabled={laedtMehr} onClick={() => void mehr()} style={{ alignSelf: 'center' }}>
              {laedtMehr ? 'Wird geladen …' : `Weitere laden (${zahl(anzahl - zeilen.length)} übrig)`}
            </ImmoButton>
          )}
        </>
      )}

      {frage !== null && (
        <BestaetigenDialog titel={stufe === 'hoch' ? 'Sehr sichere Vorschläge übernehmen?' : 'Eher sichere Vorschläge übernehmen?'} gefahr={false} icon={Checks} bestaetigenLabel={`${zahl(frage)} übernehmen`}
          text={`${zahl(frage)} PDFs von ${objektName} werden nacheinander mit vorgeschlagenem Namen und Ordner nach „Altarchiv“ im Aktenschrank übernommen.`}
          detail={<span>Nur {stufenText} Vorschläge mit Datum und Ordner. Du kannst jederzeit anhalten; das Original bleibt im OneDrive.</span>}
          onBestaetigen={() => void starteMassen()} onAbbrechen={() => setFrage(null)} />
      )}

      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
